import Chip from '@material-ui/core/Chip';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Col,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Row,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  ViewContainer,
} from 'components/AORedesign';
import { Feature } from 'components/Feature';
import featureFlags from 'constants/featureFlags';
import { ROUTE_REDESIGN_APP_ADMIN } from 'constants/routes';
import { setAppSnackbarProps } from 'redux/app-store';
import { getRuntimeConfig, updateRuntimeConfigSetting } from 'services/integration/aoc-api-runtime-config-service';

const useStyles = makeStyles((theme) => ({
  onChip: {
    backgroundColor: theme.palette.alert.successFill,
  },
  offChip: {
    backgroundColor: theme.palette.grey[200],
  },
  metaText: {
    color: theme.palette.text.secondary,
    display: 'block',
    fontSize: '0.8125rem',
  },
  categoryRow: {
    backgroundColor: theme.palette.grey[100],
  },
  // The description lives in this column, so give it the width and let the narrow metadata
  // columns keep their content on one line.
  settingCell: {
    minWidth: '18rem',
    width: '45%',
  },
  nowrapCell: {
    whiteSpace: 'nowrap',
  },
  lockedChip: {
    marginLeft: theme.spacing(1),
  },
}));

/**
 * Group the flat setting list into the categories the API assigned, preserving the order the
 * API returned them in (it sorts by category then label) rather than imposing one here.
 */
const groupByCategory = (settings = []) =>
  settings.reduce((groups, setting) => {
    const group = groups.find((candidate) => candidate.category === setting.category);
    if (group) {
      group.settings.push(setting);
    } else {
      groups.push({ category: setting.category, settings: [setting] });
    }
    return groups;
  }, []);

/**
 * The switchboard itself. Mounted only for SuperAdmins — see RuntimeConfig below — so the
 * endpoints, which are SuperAdmin-only, are never called by anyone who would get a 403.
 */
const RuntimeConfigSwitchboard = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [switchboard, setSwitchboard] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [pendingChange, setPendingChange] = useState(null);

  const dispatchSnackBarMessage = useCallback(
    (severity, message) => {
      dispatch(setAppSnackbarProps({ message, severity }));
    },
    [dispatch],
  );

  const fetchConfig = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getRuntimeConfig();
      setSwitchboard(data);
      setLoadFailed(false);
    } catch (error) {
      console.error(error);
      // Drop the stale switchboard as well as the rows, so the environment/region header cannot
      // keep claiming to describe data that is no longer on screen.
      setSwitchboard(null);
      setLoadFailed(true);
      dispatchSnackBarMessage('error', 'Error retrieving runtime configuration. See console log for details.');
    } finally {
      setIsLoading(false);
    }
  }, [dispatchSnackBarMessage]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const applyChange = async ({ key, label, enabled }) => {
    setPendingChange(null);
    setSavingKey(key);
    try {
      const updated = await updateRuntimeConfigSetting(key, enabled);
      // Patch the single row rather than refetching, so the rest of the table does not
      // flicker while one change is in flight.
      setSwitchboard((previous) => ({
        ...previous,
        settings: previous.settings.map((setting) => (setting.key === key ? { ...setting, ...updated } : setting)),
      }));
      // Name the new state in the API's own words, the way the table does, so a report cutover
      // setting confirms as "Report Service" rather than a bare "on".
      dispatchSnackBarMessage('success', `${label} is now ${updated?.stateLabel || (enabled ? 'on' : 'off')}.`);
    } catch (error) {
      console.error(error);
      dispatchSnackBarMessage('error', `Could not update ${label}. See console log for details.`);
    } finally {
      setSavingKey(null);
    }
  };

  // The API supplies the words for each state — a report cutover setting says "Report Service" /
  // "Legacy SSRS" where a plain feature says "On" / "Off" — so this renders whatever it sent.
  // A locked setting is called out here too: a greyed switch alone looks the same as an off one,
  // so without this the only clue is a tooltip nobody knows to hover.
  const renderState = (setting) => (
    <>
      <Chip size="small" label={setting.stateLabel} className={setting.enabled ? classes.onChip : classes.offChip} />
      {setting.overriddenByEnv ? <Chip size="small" variant="outlined" label="Locked" className={classes.lockedChip} /> : null}
    </>
  );

  const renderLastChanged = (setting) => {
    if (!setting.updatedAt) {
      return <span className={classes.metaText}>Never changed</span>;
    }
    return (
      <span className={classes.metaText}>
        {moment.utc(setting.updatedAt).local().format('MM/DD/YYYY h:mm A')}
        {setting.updatedBy ? ` by ${setting.updatedBy}` : ''}
      </span>
    );
  };

  const renderSwitch = (setting) => {
    const control = (
      // The switch role already conveys on/off to a screen reader, so the name is just the
      // setting — "Turn on X" would announce as "Turn on X, checked" on a setting already on.
      <Switch
        color="primary"
        checked={setting.enabled}
        disabled={setting.overriddenByEnv || savingKey !== null}
        inputProps={{ 'aria-label': setting.label }}
        onChange={(event) => setPendingChange({ key: setting.key, label: setting.label, description: setting.description, enabled: event.target.checked })}
      />
    );

    // Every disabled switch says why it is disabled. The row being saved renders a spinner
    // instead of a switch, so the only other rows here are the ones waiting on that save.
    let disabledReason = null;
    if (setting.overriddenByEnv) {
      disabledReason = `Locked by the ${setting.overrideVariable} environment variable on this deployment.`;
    } else if (savingKey !== null) {
      disabledReason = 'Another setting is being changed. Changes are saved one at a time.';
    }

    if (disabledReason) {
      return (
        <Tooltip title={disabledReason}>
          <span>{control}</span>
        </Tooltip>
      );
    }
    return control;
  };

  const renderRows = () => {
    if (isLoading && !switchboard) {
      return [0, 1, 2, 3, 4].map((index) => (
        <TableRow key={`skeleton-${index}`}>
          <TableCell className={classes.settingCell}>
            <Skeleton variant="text" width="40%" />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width={80} />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width={120} />
          </TableCell>
          <TableCell align="right">
            <Skeleton variant="text" width={40} />
          </TableCell>
        </TableRow>
      ));
    }

    if (!switchboard?.settings?.length) {
      return (
        <TableRow>
          <TableCell colSpan={4}>
            <Typography color="textSecondary">No settings are declared for this environment.</Typography>
          </TableCell>
        </TableRow>
      );
    }

    return groupByCategory(switchboard.settings).flatMap((group) => [
      <TableRow key={`category-${group.category}`} className={classes.categoryRow}>
        <TableCell colSpan={4}>
          <Typography variant="subtitle2">{group.category}</Typography>
        </TableCell>
      </TableRow>,
      ...group.settings.map((setting) => (
        <TableRow key={setting.key}>
          <TableCell className={classes.settingCell}>
            <Typography>{setting.label}</Typography>
            {setting.description ? <span className={classes.metaText}>{setting.description}</span> : null}
          </TableCell>
          <TableCell>{renderState(setting)}</TableCell>
          <TableCell className={classes.nowrapCell}>{renderLastChanged(setting)}</TableCell>
          <TableCell align="right">{savingKey === setting.key ? <CircularProgress size={20} /> : renderSwitch(setting)}</TableCell>
        </TableRow>
      )),
    ]);
  };

  return (
    <ViewContainer
      title={'Runtime Configuration'}
      returnRoute={{ text: 'Application Administration', route: ROUTE_REDESIGN_APP_ADMIN }}
      hideStartQuote
      hidePolicyViewChange>
      {/* Cards are direct children rather than wrapped in a Row: Row is a flex container, so a
          Card inside one sizes to its content and the cards end up different widths. */}
      <Box mb={2}>
        <Card>
          <Row alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Col xs={12} md={8}>
              {switchboard ? (
                <Box mb={1}>
                  <Typography variant="subtitle2">
                    {switchboard.environment} · {switchboard.region}
                  </Typography>
                </Box>
              ) : null}
              <Typography color="textSecondary">
                Changes apply to everyone in this environment and region within about a minute, with no deployment — if something misbehaves, change it back.
                Each region keeps its own settings.
              </Typography>
            </Col>
            <Col xs={12} md="auto">
              <Button variant="outlined" size="small" onClick={fetchConfig} disabled={isLoading || savingKey !== null}>
                Refresh
              </Button>
            </Col>
          </Row>
        </Card>
      </Box>

      {loadFailed ? (
        <Alert
          severity="error"
          alertTitleMessage="Runtime configuration could not be loaded"
          message="Nothing has changed — every setting is still in whatever state it was already in. Try Refresh."
        />
      ) : (
        <Card>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Setting</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Last Changed</TableCell>
                <TableCell align="right">Enabled</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{renderRows()}</TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={pendingChange !== null} onClose={() => setPendingChange(null)}>
        <DialogTitle>{`Turn ${pendingChange?.enabled ? 'on' : 'off'} ${pendingChange?.label}?`}</DialogTitle>
        <DialogContent>
          {/* What the setting does comes before what confirming will do — this is the last point
              at which an admin can tell they have the wrong switch. */}
          {pendingChange?.description ? <DialogContentText>{pendingChange.description}</DialogContentText> : null}
          <DialogContentText>
            {pendingChange?.enabled
              ? `${pendingChange?.label} will be on for all users in ${switchboard?.environment} (${switchboard?.region}). You can turn it back off at any time.`
              : `${pendingChange?.label} will be off for all users in ${switchboard?.environment} (${switchboard?.region}).`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setPendingChange(null)}>
            Cancel
          </Button>
          <Button onClick={() => applyChange(pendingChange)}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </ViewContainer>
  );
};

/**
 * Runtime configuration switchboard.
 *
 * One consolidated place for a SuperAdmin to change live settings without a deploy. Which
 * settings exist is declared by the API, not here, so a new setting appears on this screen with
 * no UI change; the API also supplies the words for each state, so a setting can name its own
 * two states ("Report Service" / "Legacy SSRS") instead of just on and off.
 *
 * Every setting the API currently declares is on/off, which is why each row renders a switch. A
 * setting with a value rather than a state — a threshold, a server name — needs a new control
 * here; the store behind it already carries strings, numbers and JSON.
 *
 * The gate is a wrapper rather than a branch inside the switchboard so that a non-SuperAdmin
 * landing on this route never mounts it, and so never fires the SuperAdmin-only requests.
 */
const RuntimeConfig = () => (
  <Feature allowed={featureFlags.ACCESS_SUPER_ADMIN}>
    <RuntimeConfigSwitchboard />
  </Feature>
);

export default RuntimeConfig;
